/**
 * Brand ranking. Items + value per brand.
 */
import { store } from '../store.ts';
import { formatINR } from '../types.ts';
import type { BrandStat } from '../types.ts';

export class AlmariBrandRank extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.by_brand, s.summary.total_value_inr);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(byBrand: Record<string, BrandStat>, total: number): void {
    const entries = Object.entries(byBrand).slice(0, 8);
    if (!entries.length) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }
    const rows = entries.map(([b, stat], i) => {
      const pct = total > 0 ? (stat.value / total) * 100 : 0;
      return /* html */ `
<div class="row" style="--pct:${pct}%">
  <div class="rank">${String(i + 1).padStart(2, '0')}</div>
  <div class="info">
    <div class="name">${escape(b)}</div>
    <div class="meta">${stat.count} piece${stat.count === 1 ? '' : 's'}</div>
    <div class="bar"><span></span></div>
  </div>
  <div class="amt">${formatINR(stat.value)}</div>
</div>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 14px;
  padding-left: 4px;
}
.list { display: grid; gap: 2px; background: var(--paper-card); border: 1px solid var(--rule); border-radius: 8px; overflow: hidden; }
.row {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
}
.row:last-child { border-bottom: none; }
.rank { font-family: var(--mono); font-size: 10px; color: var(--brass-dim); letter-spacing: 0.1em; }
.info { display: grid; gap: 4px; min-width: 0; }
.name {
  font-family: var(--display);
  font-size: 19px;
  color: var(--ink);
  font-weight: 700;
  letter-spacing: -0.01em;
}
.meta {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.bar { height: 3px; background: oklch(85% 0.04 60); border-radius: 999px; overflow: hidden; }
.bar span { display: block; height: 100%; width: var(--pct); background: var(--brass); animation: g 700ms var(--tx-snap) both; }
.amt {
  font-family: var(--display);
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
  font-feature-settings: "tnum" 1;
}
@keyframes g { from { transform: scaleX(0); transform-origin: left; } to { transform: scaleX(1); } }
</style>
<div class="title">Top labels</div>
<div class="list">${rows}</div>
`;
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('almari-brand-rank', AlmariBrandRank);
